import { z } from 'zod';
import { isMap, isScalar, isSeq, parse as parseYaml, parseDocument, stringify as stringifyYaml, visit, } from 'yaml';
/**
 * The frontmatter contract. Every page carries this block, and both the site
 * build and the search indexer read it. Validation lives here — in one place —
 * so the editor, the MCP server and CI cannot disagree about what is valid.
 */
export const frontmatterSchema = z.object({
    /**
     * Optional at PARSE time, deliberately.
     *
     * A page with no title is invalid to publish, but the editor still has to be
     * able to open it — a tool whose job includes fixing broken pages cannot
     * refuse to load them. Real GitBook exports have no frontmatter at all, so a
     * required title here meant the importer could not read a single one of them.
     * The requirement is enforced by the linter, which is where "is this
     * publishable" belongs.
     */
    title: z.string().optional(),
    description: z.string().optional(),
    /** Top-level grouping in nav.yaml. */
    section: z.string().optional(),
    /** Sub-grouping within a section. */
    group: z.string().optional(),
    /**
     * Sort key within a group. Lower comes first.
     *
     * Not an integer. Docusaurus writes `sidebar_position: 2.5` to put a page
     * between two others, and people do — it is the obvious way to insert one
     * without renumbering the rest. An integer-only key meant the importer either
     * dropped that position or truncated it, and truncating is worse: 2.5 and 2.7
     * both become 2, so two pages that were ordered are not any more.
     *
     * A float sorts exactly as well, and nothing here counts in steps of one.
     */
    order: z.number().optional(),
    /** Excluded from the published build, still visible in the writer. */
    draft: z.boolean().optional(),
    /** Old paths that must 301 here. Populated on move/rename, never by hand. */
    redirect_from: z.array(z.string()).optional(),
    /* ---- search engines and social cards --------------------------------
       All optional, and all overrides. The defaults are derived from `title`
       and `description`, because a page that has to restate its own title to
       rank is a page whose title is wrong. */
    /** Overrides the <title>, for when the on-page heading is not the best query. */
    seo_title: z.string().optional(),
    /** Overrides `description` in meta tags only. */
    seo_description: z.string().optional(),
    /** Overrides the social card image for this page. */
    social_image: z.string().optional(),
    /**
     * Points search engines at another URL as the original.
     *
     * For syndicated or duplicated content. Setting this wrong removes the page
     * from search entirely, so the linter warns when it is not an absolute URL.
     */
    canonical: z.string().optional(),
    /* ---- freshness ------------------------------------------------------
       Both optional. A page with neither is judged on when it last changed,
       which is a weaker signal but the only one that needs no author effort. */
    /**
     * When a person last checked this page against the product.
     *
     * Distinct from when the file last changed. Fixing a typo changes the file
     * and confirms nothing; reading the page beside the product confirms
     * everything and may change nothing. Only the second resets the clock.
     */
    last_reviewed: z.string().optional(),
    /**
     * How often this page deserves rechecking, in days.
     *
     * Per page because pages age at different rates: a pricing page goes stale in
     * a month, a conceptual overview can be right for years. One project-wide
     * number would either nag about the overview or ignore the pricing page.
     */
    review_every: z.number().int().positive().optional(),
    /**
     * Who is responsible for this page, as an email address.
     *
     * An ADDRESS and not a user id, because this file lives in the customer's
     * own git repository. A UUID in a markdown file means nothing to anybody
     * reading the repository, means nothing after an export and an import, and
     * means nothing to the person who opens the file on their laptop — while an
     * address is all three of readable, stable and resolvable back to a member.
     *
     * Not validated as an email here on purpose. The linter checks it against
     * the workspace's actual members, which is a better question than whether
     * it has an `@` in it, and it answers with a warning rather than a refusal:
     * people leave teams, and a publish must not be blocked because one did.
     */
    owner: z.string().optional(),
    /**
     * Keep this page out of search results.
     *
     * Distinct from `draft`: a draft is not published at all, whereas a noindex
     * page is public and reachable by link but not listed. That is what "hidden
     * but public" means, and support pages people are sent directly need it.
     */
    noindex: z.boolean().optional(),
    /**
     * This page carries words a model wrote, and nobody has reviewed them yet.
     *
     * §22: "AI-generated pages or substantial sections can carry an internal `AI
     * draft` indicator until reviewed." Frontmatter rather than a database row,
     * for the reason `last_reviewed` is frontmatter: it is a fact about the
     * CONTENT, it has to survive a commit and a push so a reviewer reading the
     * pull request sees it, and it leaves with the customer.
     *
     * Internal, not published — nothing renders frontmatter — and it says
     * "unreviewed", not "written by a machine". A writer who accepts a suggestion
     * and then reads it has reviewed it; the marker is theirs to clear, and
     * approving a review clears it for them.
     */
    ai_draft: z.boolean().optional(),
    /**
     * The issues that change what this page describes.
     *
     * A writer's own link between a page and a tracker, and the only signal in
     * this product that a person makes deliberately rather than one it infers
     * from an age or a count. When one of these ships and the page has not been
     * touched since, the inbox says so — see `packages/core/src/issue-links.ts`.
     *
     * Frontmatter rather than a table, for the reasons `owner` and
     * `last_reviewed` are: it travels with the words, it is reviewed in the draft
     * like any other change, and it leaves with the customer when they take their
     * repository. A join table would be a fact about a page kept somewhere the
     * page is not, and the first `git mv` would make it wrong.
     *
     * A key or a link, either way — `ENG-412` or the URL somebody pasted out of
     * their tracker. Asking a person to strip a URL down to a key by hand is
     * asking them to get it wrong, and the wrong answer is silent.
     */
    issues: z.array(z.string()).optional(),
});
/** What a page must satisfy to be published. Enforced by the linter. */
export const publishableFrontmatterSchema = frontmatterSchema.extend({
    title: z.string().min(1, 'a page needs a title'),
});
export const navItemSchema = z.lazy(() => z.union([
    z
        .object({
        page: z.string().min(1),
        icon: z.string().optional(),
        /**
         * The navigation's own title for this page, overriding its real one.
         *
         * Optional, and normally absent: a page's title is what shows. This
         * exists for the times it should not — Docusaurus calls it
         * `sidebar_label`, Mintlify `sidebarTitle`, and both importers carry
         * theirs in here, since a page's actual title is the wrong place for
         * text that exists ONLY to be shorter in a narrow rail.
         *
         * The GitBook importer also writes it, for a second, unrelated reason:
         * the editor's page rail has no titles to work from when it builds a
         * proposal — /api/state lists the git tree — so it falls back to
         * de-slugifying the filename, which cannot recover capitalisation the
         * slug never had and turns `what-ada-does` into "What ada does". That
         * fallback is also why the field is optional rather than required
         * alongside `page`, but everywhere it is actually consulted — the
         * writer's own rail, the published sidebar — it is checked FIRST, as
         * the override it is; a page's title is only ever the second choice.
         */
        label: z.string().optional(),
        /** Child pages, indented beneath this one. See the type above. */
        items: z.array(navItemSchema).optional(),
    })
        .strict(),
    z
        .object({
        label: z.string().min(1),
        icon: z.string().optional(),
        items: z.array(navItemSchema),
    })
        .strict(),
    /**
     * An external link. Checked as an absolute URL, because a relative one here
     * is somebody reaching for `page:` and getting a link that 404s on every
     * page except the one they tested it from.
     */
    z
        .object({
        label: z.string().min(1),
        href: z.string().regex(/^(https?:\/\/|mailto:|tel:)/, {
            message: 'href must be an absolute URL — use `page:` for a page in this site',
        }),
        icon: z.string().optional(),
    })
        .strict(),
    /**
     * A separator. `separator: true` and nothing else — a rule that is "off"
     * is not a state, so `false` is refused with the honest edit spelled out.
     * Strict, and deliberately without `icon` or `items`: an icon on a
     * horizontal rule is a typo reaching for a page or a group, and a
     * separator marks a place between items rather than containing any.
     */
    z
        .object({
        separator: z.literal(true, {
            errorMap: () => ({
                message: 'separator must be exactly `true` — to drop the rule, remove the item',
            }),
        }),
        /** A small label on the rule, e.g. "API". Optional; most rules are bare. */
        label: z.string().min(1).optional(),
    })
        .strict(),
]));
/**
 * A space, for the switcher. One entry is the normal case and renders as a
 * label rather than a dropdown; more than one and readers can move between
 * products or versions the way they can on help.adalo.com.
 */
export const navSpaceSchema = z
    .object({ label: z.string().min(1), href: z.string().min(1), current: z.boolean().optional() })
    .strict();
export const navSchema = z
    .object({
    /** Optional: a name for this space, shown above the navigation. */
    space: z.string().optional(),
    /**
     * Optional: one sentence about the site, for meta tags and the social card.
     *
     * Site-level metadata belongs here because `space` and `spaces` already do:
     * nav.yaml is where a content repo says what it IS, and the alternative was
     * a title hardcoded in the build — which meant every published site carried
     * one customer's name.
     */
    description: z.string().optional(),
    /** Optional: other spaces a reader can switch to. */
    spaces: z.array(navSpaceSchema).optional(),
    sections: z.array(z
        .object({
        label: z.string().min(1),
        icon: z.string().optional(),
        items: z.array(navItemSchema),
    })
        .strict()),
})
    .strict();
/** Fields the schema wants as a string, a number, or a boolean. */
const STRING_KEYS = new Set([
    'title',
    'description',
    'section',
    'group',
    'seo_title',
    'seo_description',
    'social_image',
    'canonical',
    'last_reviewed',
    'owner',
]);
const NUMBER_KEYS = new Set(['order', 'review_every']);
const BOOLEAN_KEYS = new Set(['draft', 'noindex', 'ai_draft']);
/**
 * Make a real repository's frontmatter usable without losing any of it.
 *
 * Only where the intent is unambiguous. `title: 2026` is a page about the year,
 * and YAML hands it over as a number; `order: "3"` is the same order as `3`;
 * `redirect_from: /old` is a list of one. None of those is a decision, and
 * refusing them is not caution.
 *
 * This is a tool whose job includes fixing broken pages, so it has to be able to
 * OPEN one. Before this, a page titled `2026` could not be loaded at all: the
 * parse threw, the editor showed nothing, and the only way to fix the page was
 * to edit the file in git — which is the thing the product exists to avoid.
 */
function coerce(raw) {
    const out = {};
    for (const [key, value] of Object.entries(raw)) {
        // `key:` with nothing after it means the key is not set. Keeping the null
        // would fail every optional field it touches.
        if (value === null)
            continue;
        if (STRING_KEYS.has(key)) {
            if (typeof value === 'number' || typeof value === 'boolean') {
                out[key] = String(value);
                continue;
            }
            // A YAML timestamp, which some writers produce for `last_reviewed`. The
            // date part is what the field means; the rest is noise from a parser.
            if (value instanceof Date && !Number.isNaN(value.getTime())) {
                out[key] = value.toISOString().slice(0, 10);
                continue;
            }
        }
        if (NUMBER_KEYS.has(key) && typeof value === 'string' && /^-?\d+$/.test(value.trim())) {
            out[key] = Number(value.trim());
            continue;
        }
        if (BOOLEAN_KEYS.has(key)) {
            const spelled = typeof value === 'string' ? value.trim().toLowerCase() : value;
            if (spelled === 'true' || spelled === 'yes' || spelled === 1) {
                out[key] = true;
                continue;
            }
            if (spelled === 'false' || spelled === 'no' || spelled === 0) {
                out[key] = false;
                continue;
            }
        }
        // `issues: ENG-412` is a list of one, the same as `redirect_from: /old`.
        if (key === 'issues' && typeof value === 'string') {
            out[key] = [value];
            continue;
        }
        if (key === 'redirect_from' && typeof value === 'string') {
            out[key] = [value];
            continue;
        }
        out[key] = value;
    }
    return out;
}
/**
 * Parse, and do not refuse.
 *
 * A YAML syntax error still throws — the file is not frontmatter and nothing can
 * be done with it. But a value of the wrong TYPE is returned as it stands rather
 * than rejected, because rejecting it makes the page unopenable and the product
 * useless for exactly the content it is meant to repair. Saying "this is not
 * publishable" is the linter's job, and it says which key and why.
 */
export function parseFrontmatter(yaml) {
    const raw = yaml.trim() === '' ? {} : parseYaml(yaml);
    if (raw === null || raw === undefined)
        return {};
    if (typeof raw !== 'object' || Array.isArray(raw)) {
        // Frontmatter that is a list or a scalar is not a mapping and has no keys
        // to preserve. The linter reports it; this returns nothing rather than
        // inventing a shape.
        return {};
    }
    const coerced = coerce(raw);
    // `passthrough`, so a key this product has never heard of survives.
    const checked = frontmatterSchema.passthrough().safeParse(coerced);
    return (checked.success ? checked.data : coerced);
}
/**
 * Emission order, DERIVED from the schema rather than restated beside it.
 *
 * It used to be a hand-written list of seven keys while the schema declared
 * sixteen, so nine were parsed, validated, and then thrown away on write —
 * including `last_reviewed` and `review_every`, which is to say the freshness
 * signal this entire product is built on. A writer marked a page reviewed, the
 * page saved, and the mark was gone.
 *
 * Nothing caught it, because the round-trip test compares against the
 * canonicalised form and canonicalising is what dropped them. So the list is now
 * the schema's own key order: a second list beside a schema is a list that will
 * drift, and this one did.
 *
 * Order still matters — frontmatter must serialize deterministically or every
 * save produces a diff nobody asked for and the fixed-point test never settles.
 */
const KNOWN_KEYS = Object.keys(frontmatterSchema.shape);
export function serializeFrontmatter(fm, previous) {
    const ordered = {};
    for (const key of KNOWN_KEYS) {
        const value = fm[key];
        if (value !== undefined)
            ordered[key] = value;
    }
    // Then whatever else the file carried, alphabetically. Sorted rather than in
    // the order it arrived, because "the order it arrived" is not available after
    // a parse and a guess would make the output unstable.
    for (const key of Object.keys(fm)
        .filter((key) => !KNOWN_KEYS.includes(key))
        .sort()) {
        const value = fm[key];
        if (value !== undefined)
            ordered[key] = value;
    }
    /**
     * Written for a YAML 1.1 reader, read as YAML 1.2.
     *
     * The asymmetry is deliberate. Frontmatter leaves this system: Astro parses it
     * with 1.1 semantics, where an unquoted `2026-01-01` is a TIMESTAMP and
     * `last_reviewed: z.string()` then fails the site build. Emitting under the
     * 1.1 schema quotes anything that would change meaning, so the file says the
     * same thing to both.
     *
     * Parsing stays on the default 1.2 core schema, which reads that same
     * unquoted date as a string — which is what the field is. Parsing as 1.1 would
     * hand the validator a Date and reject a file somebody's previous tool wrote.
     *
     * The cost is that a key which is a 1.1 boolean — `n`, `y`, `on`, `off` — gets
     * quoted. That is valid YAML and reads identically everywhere; a date silently
     * becoming a Date does not.
     */
    /**
     * Nothing to say is said with nothing.
     *
     * `stringifyYaml({})` is `{}` — valid YAML for an empty mapping, and what a
     * page with no frontmatter was silently given: every page written without one
     * came back carrying a literal `{}` block in the customer's repository. It
     * reparsed correctly, which is why nothing caught it; it also looked broken to
     * anybody reading the file, which is what a documentation product must not do
     * to somebody's documentation. Found by a browser gate whose editor content
     * began `---{}---`.
     *
     * The empty string here, and `serializeDocument` leaves the fence off
     * entirely when what comes back is empty — so a page with no frontmatter
     * stays a page with no frontmatter. When the file carried COMMENTS and no
     * fields, they are still restored below, and the fence stays for their sake.
     */
    if (Object.keys(ordered).length === 0) {
        /**
         * No fields — but perhaps comments, which are the customer's words and
         * outlive every field they were written beside. Only the comment lines are
         * kept: a note placed above a field that has just been removed has nothing
         * left to sit on, and `keepMapComments` would try to place it against a
         * document that is now empty (it emitted the string `null`).
         */
        if (previous === undefined)
            return '';
        return previous
            .split('\n')
            .filter((line) => line.trim() === '' || line.trimStart().startsWith('#'))
            .join('\n')
            .trim();
    }
    const text = stringifyYaml(ordered, { lineWidth: 0, schema: 'yaml-1.1' }).trimEnd();
    /**
     * `previous` is the frontmatter of the very file being rewritten, so a note
     * somebody left beside a field — "checked by legal in March, ask before
     * changing the wording" — is still there after the next save. It is the same
     * promise nav.yaml and variables.yaml keep, on the surface where it is
     * broken most often: every page, every save.
     */
    return previous === undefined ? text : keepMapComments(text, previous).trimEnd();
}
/**
 * Where a problem is, in the words the file uses.
 *
 * `sections.0.items.2` is a path into a data structure. "item 3 in section 1
 * (Guides)" is a place in a file somebody wrote, and it is the difference
 * between a message they can act on and one they scroll past.
 */
function placeOf(path, raw) {
    const section = typeof path[1] === 'number' ? path[1] : null;
    if (section === null)
        return 'nav.yaml';
    const sections = raw?.sections;
    const label = sections?.[section]?.label;
    const named = typeof label === 'string' && label !== '' ? ` (${label})` : '';
    const at = `section ${section + 1}${named}`;
    const item = path[2] === 'items' && typeof path[3] === 'number' ? path[3] : null;
    return item === null ? at : `item ${item + 1} in ${at}`;
}
/**
 * What each kind of item is recognised BY, and what it needs beside that.
 *
 * The key that names the kind is the one somebody reaches for first — they
 * write `href:` because they want a link — so it is the honest way to read
 * intent from a half-written item, and it is what lets a refusal say "which
 * also needs a label" instead of "not understood".
 */
const KIND_KEYS = [
    { key: 'page', what: 'a page', needs: [], allows: ['page', 'icon', 'label', 'items'] },
    { key: 'href', what: 'a link', needs: ['label'], allows: ['href', 'label', 'icon'] },
    { key: 'separator', what: 'a separator', needs: [], allows: ['separator', 'label'] },
    { key: 'items', what: 'a group', needs: ['label'], allows: ['items', 'label', 'icon'] },
];
/** What an item may be, said once and used in every message that needs it. */
const ITEM_SHAPES = 'a page (`- page: guides/install`), a link (`- label: Status` with `href:`), ' +
    'a group (`- label: Guides` with `items:`), or a separator (`- separator: true`)';
/**
 * One Zod issue, as a sentence.
 *
 * The union case is the one that matters. A navigation item may be four
 * different shapes, so Zod reports a failure as `invalid_union` carrying one
 * error per shape it tried — about ninety lines of JSON for a single wrong
 * line, which is what a refused commit used to answer with. Nobody can act on
 * that, and the person meeting it is usually somebody hand-writing their
 * first nav.yaml.
 */
function sentenceFor(issue, raw) {
    const place = placeOf(issue.path, raw);
    if (issue.code === 'unrecognized_keys') {
        const keys = issue.keys.map((k) => `\`${k}\``).join(', ');
        // `title` for `label` is the mistake people actually make, and saying so
        // is worth more than listing what is allowed.
        const hint = issue.keys.includes('title') ? ' — a section names itself with `label`' : '';
        const allowed = issue.path.length <= 2 ? '`label`, `icon` and `items`' : '`page`, `icon` and `label`';
        return `${place} has ${keys}, which does not belong here${hint}. It takes ${allowed}.`;
    }
    if (issue.code === 'invalid_union') {
        const found = valueAt(raw, issue.path);
        /**
         * A page name that YAML read as a number.
         *
         * `page: 2024` in a changelog is a real thing people write, and YAML
         * hands it over as a number, so the union fails and the generic message
         * says nothing about the actual mistake. The fix is quoting it, so that
         * is what the sentence says.
         */
        const page = found?.page;
        if (page !== undefined && typeof page !== 'string') {
            return `${place} has \`page: ${String(page)}\`, which YAML read as ${typeof page}. A page name is text — quote it: \`page: '${String(page)}'\`.`;
        }
        /**
         * Somebody who almost had it right is told what is missing, not that
         * nothing was understood.
         *
         * The generic message is the correct answer to "this is not a navigation
         * item". It is the wrong answer to "this is a link with no label", which
         * is a different and much more common mistake — and telling somebody who
         * was one key away to start again is how a refusal stops being read.
         *
         * The intent is read from the key that names the KIND, because that is
         * what the author reached for: `page`, `href`, `items` or `separator`.
         */
        const written = new Set(found !== null && typeof found === 'object' ? Object.keys(found) : []);
        const kind = KIND_KEYS.find(({ key }) => written.has(key));
        if (kind !== undefined) {
            // A rule that recognised the shape and refused it keeps its own words:
            // `separator: false` says the honest edit, which is to remove the item.
            for (const attempt of issue.unionErrors) {
                for (const inner of attempt.issues) {
                    if (inner.path.at(-1) === kind.key && inner.code === 'invalid_literal') {
                        return `${place}: ${inner.message}.`;
                    }
                }
            }
            const missing = kind.needs.filter((key) => !written.has(key));
            if (missing.length > 0) {
                const list = missing.map((key) => `\`${key}\``).join(' and ');
                return `${place} looks like ${kind.what}, which also needs ${list}.`;
            }
            const spare = [...written].filter((key) => !kind.allows.includes(key));
            if (spare.length > 0) {
                return `${place} looks like ${kind.what}, and has ${spare
                    .map((key) => `\`${key}\``)
                    .join(', ')}, which ${spare.length === 1 ? 'does' : 'do'} not belong there.`;
            }
        }
        if (typeof found === 'string') {
            return `${place} is the text \`${found}\` — write a page as \`- page: ${found}\`.`;
        }
        return `${place} is not something the navigation understands.`;
    }
    if (issue.code === 'invalid_type' && issue.received === 'undefined') {
        const key = issue.path.at(-1);
        return `${place} has no \`${String(key)}\`, and needs one.`;
    }
    if (issue.code === 'invalid_type') {
        const key = issue.path.at(-1);
        return `${place}: \`${String(key)}\` should be ${issue.expected}, not ${issue.received}.`;
    }
    return `${place}: ${issue.message}`;
}
function valueAt(raw, path) {
    let at = raw;
    for (const step of path) {
        if (at === null || typeof at !== 'object')
            return undefined;
        at = at[step];
    }
    return at;
}
/**
 * Read nav.yaml, and refuse it in words.
 *
 * A refused commit used to answer with the raw Zod dump — `invalid_union`,
 * `unionErrors`, ninety lines of JSON — which is a product telling somebody
 * their site will not publish and giving them nothing to do about it. Found
 * by writing a nav.yaml by hand and having to read the schema to work out
 * what was wrong with it.
 *
 * Three problems at most. A file with one mistake usually has one message; a
 * file with twenty has one mistake repeated, and printing all twenty buries
 * it again.
 */
export function parseNav(yaml) {
    const raw = parseYaml(yaml) ?? { sections: [] };
    const read = navSchema.safeParse(raw);
    if (read.success)
        return read.data;
    /**
     * One sentence per PLACE, worst first, and at most three.
     *
     * A wrong section produces several issues at once — no `label`, an
     * unrecognised `title`, and a failure for every item inside it — and
     * printing them all rebuilds the wall this replaced. The most useful one
     * for a place is the one that names what was written rather than what was
     * missing, because that is the line somebody has to go and change.
     */
    const rank = (issue) => issue.code === 'unrecognized_keys' ? 0 : issue.code === 'invalid_union' ? 1 : 2;
    const best = new Map();
    for (const issue of read.error.issues) {
        const place = placeOf(issue.path, raw);
        const held = best.get(place);
        if (held === undefined || rank(issue) < rank(held))
            best.set(place, issue);
    }
    const places = [...best.values()];
    const sentences = places.slice(0, 3).map((issue) => sentenceFor(issue, raw));
    const more = places.length > 3 ? ` (and ${places.length - 3} more).` : '';
    // The shapes, once at the end rather than in every sentence — three copies
    // of the same list is the wall again in a different shape.
    const shapes = sentences.some((said) => /understands|write a page as|quote it/i.test(said))
        ? ` Every item is ${ITEM_SHAPES}.`
        : '';
    throw new Error(sentences.join(' ') + more + shapes);
}
/**
 * Write nav.yaml, keeping the comments in the file it replaces.
 *
 * Every write of this file went through `stringify` on a plain object, which
 * cannot know about comments, so a drag in the sidebar — or a rename, or the
 * reference generator adding a group — silently deleted every line of prose in
 * the customer's own navigation. Including the two the product seeds the file
 * with, which a new project loses the first time it uses the product on it.
 * The content repository is theirs; rewriting a file in it is a promise to
 * hand it back recognisable.
 *
 * `previous` is optional because one caller genuinely has nothing to preserve:
 * the importer PROPOSES a navigation for a project that had none.
 */
/**
 * Keep the comments when a YAML file this product owns the SHAPE of, but not
 * the contents of, is rewritten.
 *
 * Shared by nav.yaml and redirects.yaml because it is one job: both are lists
 * of entries a person may have annotated, both are rewritten whole, and both
 * have an entry identity that survives reordering — a page, a group label, or
 * the address a redirect is FROM.
 */
export function keepListComments(nextText, previous) {
    let before;
    try {
        before = parseDocument(previous);
    }
    catch {
        return nextText;
    }
    const carried = commentsIn(before);
    if (carried.size === 0 && !before.comment && !before.commentBefore)
        return nextText;
    const after = parseDocument(nextText);
    place(after, carried);
    if (before.commentBefore)
        after.commentBefore = before.commentBefore;
    if (before.comment)
        after.comment = before.comment;
    return String(after);
}
export function serializeNav(nav, previous) {
    const text = stringifyYaml(nav, { lineWidth: 0 }).trimEnd();
    /**
     * The file's header and footer hang on the document rather than on any node
     * in it, and the parser draws the line people write: a comment with a blank
     * line under it heads the FILE and stays at the top; one sitting directly
     * above a key is about that key and travels with it. All of that is in
     * `keepListComments`, which redirects.yaml needs too.
     */
    return previous === undefined ? text : keepListComments(text, previous).trimEnd();
}
/**
 * What a nav item IS, so a comment can find it again after a move.
 *
 * The item's own content, never its position: the whole point is that a
 * sentence written above the pricing page stays above the pricing page when
 * somebody drags it into another section.
 */
function identityOf(node) {
    const page = node.get('page');
    if (typeof page === 'string')
        return `page:${page}`;
    const label = node.get('label');
    if (typeof label === 'string')
        return `label:${label}`;
    // A redirect, in the other file this walks: the address somebody is arriving
    // at is what the rule is about, and duplicates are already refused.
    const from = node.get('from');
    if (typeof from === 'string')
        return `from:${from}`;
    // A separator has neither, and two separators are indistinguishable. A
    // comment on one is dropped rather than guessed at.
    return null;
}
const valueOf = (node) => node?.value;
const pairNamed = (node, ...names) => node.items.find((pair) => names.includes(String(valueOf(pair.key))));
/**
 * Every comment in a nav.yaml, keyed by whatever it was attached to.
 *
 * Walked with the parent in hand rather than with `visit`, because the anchor
 * a comment needs depends on where it sat. A note above the FIRST entry of a
 * list belongs to the list — that is where the parser hangs it, and it is how
 * people write a heading for a group — so it is keyed to the list and stays at
 * the top of it. A note above any other entry belongs to that entry and
 * travels with it.
 *
 * An identity that appears twice is dropped rather than carried. Two groups
 * may honestly share a label, and putting one's note above the other is worse
 * than losing it: a wrong sentence about somebody's navigation is read as
 * true.
 */
function commentsIn(doc) {
    const found = new Map();
    /**
     * How many nodes answer to each identity, whether or not they carry a
     * comment.
     *
     * Counted rather than detected on collision, because the dangerous shape is
     * ONE commented group and one uncommented group of the same name: nothing
     * collides while collecting, and placement then writes the comment above
     * both of them. Two identical notes about two different groups is the
     * misattribution this exists to prevent, and it is the likelier accident.
     */
    const howMany = new Map();
    const keep = (key, carried) => {
        // A node with nothing on it makes no claim on the key.
        if (!carried || (!carried.before && !carried.trailing && !carried.spaced))
            return;
        found.set(key, carried);
    };
    const readList = (seq, listKey) => {
        if (!isSeq(seq))
            return;
        const before = seq.commentBefore;
        if (before)
            keep(`${listKey}#top`, { before });
        for (const entry of seq.items) {
            if (!isMap(entry))
                continue;
            const id = identityOf(entry);
            if (id === null)
                continue;
            howMany.set(id, (howMany.get(id) ?? 0) + 1);
            keep(id, {
                ...(entry.commentBefore ? { before: entry.commentBefore } : {}),
                ...(entry.spaceBefore ? { spaced: true } : {}),
            });
            /**
             * A trailing note hangs on the FIELD whose line it follows, and which
             * field that is differs by file: `- page: x # note` in a navigation,
             * `to: /pricing # agreed with sales` in a redirect. Keyed per field
             * rather than assumed, so neither file needs the other one's shape.
             */
            for (const pair of entry.items) {
                const field = valueOf(pair.key);
                const note = pair.value?.comment;
                if (typeof field === 'string' && note)
                    keep(`${id}/${field}`, { trailing: note });
            }
            readList(pairNamed(entry, 'items')?.value, id);
        }
    };
    if (isSeq(doc.contents))
        readList(doc.contents, 'root');
    if (isMap(doc.contents)) {
        for (const pair of doc.contents.items) {
            const name = valueOf(pair.key);
            if (typeof name !== 'string')
                continue;
            keep(`key:${name}`, {
                ...(pair.key?.commentBefore
                    ? { before: pair.key.commentBefore }
                    : {}),
                ...(pair.key?.spaceBefore ? { spaced: true } : {}),
                ...(pair.value?.comment
                    ? { trailing: pair.value.comment }
                    : {}),
            });
            if (name === 'sections')
                readList(pair.value, 'key:sections');
        }
    }
    // Anything ambiguous goes, along with the note at the top of ITS list: a
    // list hanging under a name that means two places is no safer to write into.
    for (const [id, count] of howMany) {
        if (count === 1)
            continue;
        found.delete(id);
        found.delete(`${id}#top`);
        // The per-field notes hang off the same ambiguous identity, so they go with
        // it: a note on the `to:` of one of two identical rules is no safer.
        for (const key of [...found.keys()])
            if (key.startsWith(`${id}/`))
                found.delete(key);
    }
    return found;
}
/** Put the carried comments back on whatever now holds the same thing. */
function place(doc, carried) {
    const onNode = (node, held) => {
        if (!held || !node)
            return;
        if (held.before)
            node.commentBefore = held.before;
        if (held.spaced)
            node.spaceBefore = true;
    };
    const writeList = (seq, listKey) => {
        if (!isSeq(seq))
            return;
        const top = carried.get(`${listKey}#top`);
        if (top?.before)
            seq.commentBefore = top.before;
        for (const entry of seq.items) {
            if (!isMap(entry))
                continue;
            const id = identityOf(entry);
            if (id === null)
                continue;
            onNode(entry, carried.get(id));
            for (const pair of entry.items) {
                const field = valueOf(pair.key);
                if (typeof field !== 'string' || !pair.value)
                    continue;
                const note = carried.get(`${id}/${field}`)?.trailing;
                if (note)
                    pair.value.comment = note;
            }
            writeList(pairNamed(entry, 'items')?.value, id);
        }
    };
    if (isSeq(doc.contents))
        writeList(doc.contents, 'root');
    if (isMap(doc.contents)) {
        for (const pair of doc.contents.items) {
            const name = valueOf(pair.key);
            if (typeof name !== 'string')
                continue;
            const held = carried.get(`key:${name}`);
            onNode(pair.key, held);
            if (held?.trailing && pair.value)
                pair.value.comment = held.trailing;
            if (name === 'sections')
                writeList(pair.value, 'key:sections');
        }
    }
}
/** Every page path referenced by the nav, depth-first, in document order. */
export function navPages(nav) {
    const out = [];
    const walk = (items) => {
        for (const item of items) {
            if ('page' in item)
                out.push(item.page);
            // A parent PAGE walks its children too — `else` here silently dropped
            // every nested page from lint's and the build's view of the nav.
            // An external link has neither `page` nor `items`; reaching for
            // `item.items` on one used to throw, so the guard stays.
            if ('items' in item && item.items)
                walk(item.items);
        }
    };
    for (const section of nav.sections)
        walk(section.items);
    return out;
}
/* ---- variables ----------------------------------------------------------
   Reusable values — a product name, a version, a support URL — written once
   in `variables.yaml` at the content root and referenced from page text as
   `{{name}}`. In the CONTENT repo deliberately, not in the database: renaming
   the product is then a content change like any other — it lands on a draft,
   shows up in review, publishes with everything else, and an export carries
   it. The database would make it invisible to all four. */
/**
 * What a variable may be called: `product_name`, not `Product Name`.
 *
 * Lowercase letters, digits and underscores, starting with a letter. The same
 * shape everywhere on purpose: this parser, the `{{name}}` reference the
 * renderer substitutes, and the lint rule that reports an undefined one. A
 * brace pair that does not match the pattern is not a reference at all, which
 * is what keeps `{{ }}` in somebody's Jinja code sample out of this feature.
 */
export const VARIABLE_NAME = /^[a-z][a-z0-9_]*$/;
/**
 * Validate a mapping that is already parsed. The editor sends JSON; the YAML
 * file goes through `parseVariables`, which calls this. One validator, so the
 * two cannot disagree about what a legal variable is.
 *
 * Scalar values are coerced the way frontmatter's `coerce` does: YAML hands
 * `version: 2.5` over as the number 2.5, and the string "2.5" is the only
 * thing a person could have meant. Anything with SHAPE — a list, a nested
 * mapping, a bare `name:` with nothing after it — is refused by name, because
 * there is no one string it could stand for and guessing would put the guess
 * on every page that references it.
 */
export function validateVariables(raw) {
    const out = {};
    for (const [name, value] of Object.entries(raw)) {
        if (!VARIABLE_NAME.test(name)) {
            throw new Error(`"${name}" is not a variable name — use lowercase letters, digits and ` +
                `underscores, starting with a letter (like product_name)`);
        }
        if (typeof value === 'string') {
            out[name] = value;
            continue;
        }
        if (typeof value === 'number' || typeof value === 'boolean') {
            out[name] = String(value);
            continue;
        }
        throw new Error(`"${name}" must be one plain value, like \`${name}: Adalo\` — not a list, ` +
            `a mapping, or an empty entry`);
    }
    return out;
}
/**
 * Parse `variables.yaml`, strictly.
 *
 * Strict like `parseNav` and unlike `parseFrontmatter`, and the difference is
 * who gets hurt: a broken page hurts the page, so the editor opens it anyway,
 * but a broken variables file would change what EVERY page that says
 * `{{name}}` renders as. Refusing with a readable message is the kind answer.
 * An empty or absent file is not broken — it means no variables, and that is
 * an ordinary state every new project starts in.
 */
export function parseVariables(yaml) {
    const raw = yaml.trim() === '' ? {} : parseYaml(yaml);
    if (raw === null || raw === undefined)
        return {};
    if (typeof raw !== 'object' || Array.isArray(raw)) {
        throw new Error('variables.yaml must be a mapping of name to value, like `product_name: Adalo`');
    }
    return validateVariables(raw);
}
/**
 * Deterministic emission: sorted by name, quoted where YAML would otherwise
 * change the meaning. Sorted rather than insertion-ordered because every
 * writer of this file sends a whole map, and "the order it arrived" would
 * make two saves of the same variables a diff nobody asked to review.
 *
 * `yaml-1.1` for the reason `serializeFrontmatter` gives: a value that LOOKS
 * like a number or a date must come out quoted, so the file says the same
 * string to every reader.
 */
export function serializeVariables(variables, previous) {
    const ordered = {};
    for (const name of Object.keys(variables).sort())
        ordered[name] = variables[name];
    const text = stringifyYaml(ordered, { lineWidth: 0, schema: 'yaml-1.1' });
    return previous === undefined ? text : keepMapComments(text, previous);
}
/**
 * Carry the comments of a flat YAML map across a rewrite of it.
 *
 * variables.yaml is the same promise nav.yaml is: a file in the CUSTOMER's
 * repository that this product rewrites whole on every save. It is also where
 * the notes are load-bearing — "bumped by the release script, do not edit by
 * hand" is exactly the sentence somebody puts beside a version, and exactly
 * the one that was being deleted by the save that made them look.
 *
 * Simpler than the navigation's: a mapping key is unique by definition, so a
 * comment can always be put back where it belongs, and the sort this file gets
 * on every save just carries each note along with its own key.
 */
export function keepMapComments(nextText, previous) {
    let before;
    try {
        before = parseDocument(previous);
    }
    catch {
        // A file nobody can parse has no comments anybody can place.
        return nextText;
    }
    /**
     * Keyed by the PATH of key names, not by the name alone.
     *
     * search.yaml is the file that needs the depth: a note belongs above one
     * alias or one pinned result, and `aliases > log in` and `pinned > log in`
     * are two different places that a bare name would collide on. Flat files —
     * variables.yaml, a page's frontmatter — have paths one segment long and
     * behave exactly as before.
     */
    const held = new Map();
    const read = (map, path) => {
        if (!isMap(map))
            return;
        for (const pair of map.items) {
            const name = valueOf(pair.key);
            if (typeof name !== 'string')
                continue;
            const here = path === '' ? name : `${path} > ${name}`;
            const carried = {
                ...(pair.key?.commentBefore
                    ? { before: pair.key.commentBefore }
                    : {}),
                ...(pair.key?.spaceBefore ? { spaced: true } : {}),
                ...(pair.value.comment
                    ? { trailing: pair.value.comment }
                    : {}),
            };
            if (carried.before || carried.trailing || carried.spaced)
                held.set(here, carried);
            /**
             * A note above the FIRST key of a nested block hangs on the block, not
             * on that key — the same place the parser puts it for a list, and the
             * same rule follows: it heads that block and stays at the head of it.
             */
            const heads = pair.value?.commentBefore;
            if (heads && isMap(pair.value))
                held.set(`${here}#top`, { before: heads });
            read(pair.value, here);
        }
    };
    read(before.contents, '');
    /**
     * The file's own header and footer, which hang on the document rather than
     * on any entry in it.
     *
     * The parser draws the line people actually write: a comment with a blank
     * line under it is a header for the file, and a comment sitting directly
     * above a key is about that key. So a header stays at the top through a
     * re-sort, and a note travels with the entry it describes — without this
     * product having to guess which was meant.
     */
    const header = before.commentBefore;
    const tail = before.comment;
    if (held.size === 0 && !tail && !header)
        return nextText;
    const after = parseDocument(nextText);
    const write = (map, path) => {
        if (!isMap(map))
            return;
        for (const pair of map.items) {
            const name = valueOf(pair.key);
            if (typeof name !== 'string')
                continue;
            const here = path === '' ? name : `${path} > ${name}`;
            const carried = held.get(here);
            if (carried) {
                if (carried.before)
                    pair.key.commentBefore = carried.before;
                if (carried.spaced)
                    pair.key.spaceBefore = true;
                /**
                 * Placed only onto a SCALAR.
                 *
                 * The shape a value is written in can change between one save and the
                 * next — a pinned page written as `billing: pages/x.md` is re-emitted
                 * as a list once a second page is pinned — and hanging the old note on
                 * the new list puts it after the last item, where it reads as a remark
                 * about nothing.
                 *
                 * Guarded here rather than when reading. The same guard on the way in
                 * looked right and was unreachable: nothing this writes turns a list
                 * back into a scalar, so removing it changed no behaviour and no test,
                 * which makes it a claim nobody can check.
                 */
                if (carried.trailing && isScalar(pair.value)) {
                    ;
                    pair.value.comment = carried.trailing;
                }
            }
            const heads = held.get(`${here}#top`)?.before;
            if (heads && isMap(pair.value)) {
                ;
                pair.value.commentBefore = heads;
            }
            write(pair.value, here);
        }
    };
    write(after.contents, '');
    if (header)
        after.commentBefore = header;
    if (tail)
        after.comment = tail;
    return String(after);
}
export { THEME_PRESETS, DEFAULT_PRESET, presetProblem, presetOrDefault, } from './theme-presets.js';
export { parseSearchTuning, serializeSearchTuning, EMPTY_TUNING, } from './search-tuning.js';
export { parseRedirects, resolveRedirect, serializeRedirects } from './redirects.js';
export { CODE_THEMES, DEFAULT_CODE_THEME, codeThemeOrDefault, codeThemeProblem, } from './code-themes.js';
export { FONT_PAIRINGS, DEFAULT_FONTS, fontsOrDefault, fontsProblem, } from './fonts.js';
export { parseGlossary, serializeGlossary, EMPTY_GLOSSARY, GLOSSARY_STARTER, } from './glossary.js';
export { parseStyleGuide, serializeStyleGuide, EMPTY_STYLE, STYLE_STARTER, MOST_RULES, LONGEST_RULE, } from './style.js';
export { retriesFor, EMPTY_RETRY } from './search-retry.js';
export { DATA_FILES, NAV_FILE, SITE_FILES, isSiteFile, } from './site-files.js';
//# sourceMappingURL=index.js.map